// XLSX bulk-upload sub-flow for the stock-import "Add items" step.
//
// Two modes supported by the backend:
//   1. "single": file is a flat list of serials → must come with a chosen variant.
//   2. "multi":  file has variant_sku + serial_no columns → backend groups by sku.
//
// The UI picks mode based on whether the user has already selected a variant
// in the manual form. If yes we send variantId; if no we expect the file itself
// to carry the SKU column.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Download } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";

interface VariantContext {
  id: string;
  sku: string;
  productName: string;
}

interface Props {
  importId: string;
  /** Optional — when provided, mode="single" with this variant; else "multi". */
  variant?: VariantContext | null;
  /** Optional unit cost for single mode. */
  unitCost?: string;
  onDone?: () => void;
}

interface RowReport {
  row: number;
  status: "ok" | "skipped" | "failed" | "dup_stock" | "dup_sold";
  message?: string;
  key?: string;
}

interface BulkResult {
  mode: "single" | "multi";
  total: number;
  accepted: number;
  skipped: number;
  failed: number;
  // Đối chiếu với hệ thống: số đã tồn tại (file hãng dính IMEI đơn trước).
  dupStock: number;
  dupSold: number;
  itemsCreated: number;
  items?: Array<{ variantId: string; variantSku: string; serials: number; dupCount: number }>;
  rows: RowReport[];
}

// Xuất danh sách IMEI trùng ra CSV (BOM UTF-8) — bằng chứng đối chiếu với hãng.
function exportDupsCsv(result: BulkResult) {
  const dups = result.rows.filter((r) => r.status === "dup_stock" || r.status === "dup_sold");
  const head = ["Dòng trong file", "IMEI", "Tình trạng trong hệ thống"];
  const lines = dups.map((r) => [r.row || "", r.key ?? "", r.message ?? ""]);
  const csv = [head, ...lines]
    .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "imei-trung-doi-chieu-hang.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export function SerialXLSXUpload({ importId, variant, unitCost, onDone }: Props) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("no file");
      const fd = new FormData();
      fd.append("file", file);
      if (variant) fd.append("variantId", variant.id);
      if (variant && unitCost) fd.append("unitCost", unitCost);
      const res = await apiClient.post<{ data: BulkResult }>(
        `/v1/imports/${importId}/items/bulk-xlsx`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 10 * 60 * 1000 },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["import-detail", importId] });
      onDone?.();
    },
  });

  // "Hàng đếm đủ" — file hãng trùng N số nhưng máy thật vẫn nhận đủ: thêm dòng
  // bù N máy KHÔNG kèm IMEI để tồn kho + tiền đúng số thật; IMEI thật gắn bổ
  // sung khi bán tới máy (nút ở màn xem phiếu đã nhập).
  const addFillerLine = useMutation({
    mutationFn: (p: { variantId: string; qty: number }) =>
      apiClient.post(`/v1/imports/${importId}/items`, {
        variantId: p.variantId,
        quantity: p.qty,
        unitCost: variant && unitCost ? unitCost : "0",
        note: "Chờ chít IMEI bổ sung (file hãng trùng/thiếu số)",
        serials: [],
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["import-detail", importId] }),
  });

  const errorMsg = upload.error ? translateApiError(upload.error) : null;
  const result = upload.data;
  const failures = result?.rows.filter((r) => r.status !== "ok") ?? [];
  const dupTotal = (result?.dupStock ?? 0) + (result?.dupSold ?? 0);
  const fillerCandidates = (result?.items ?? []).filter((it) => it.dupCount > 0);

  return (
    <div className="rounded-md border bg-muted/20 p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium flex items-center gap-1.5">
            <FileSpreadsheet size={14} /> Upload Excel
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {variant
              ? <>Chế độ <b>1 variant</b>: file chỉ cần 1 cột serial. Gán vào: <span className="font-mono">{variant.sku}</span></>
              : <>Chế độ <b>nhiều variant</b>: file cần 2 cột — <span className="font-mono">variant_sku</span> + <span className="font-mono">serial_no</span></>}
          </p>
        </div>
        <button
          type="button"
          onClick={() => downloadTemplate(!!variant)}
          className="text-xs text-primary hover:underline flex items-center gap-1 whitespace-nowrap"
        >
          <Download size={12} /> Tải template
        </button>
      </div>

      <input
        type="file"
        accept=".xlsx,.csv"
        onChange={(e) => {
          setFile(e.target.files?.[0] ?? null);
          upload.reset();
        }}
        className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
      />

      {file && !result && (
        <p className="text-xs text-muted-foreground">
          {file.name} · {(file.size / 1024).toFixed(1)} KB
        </p>
      )}

      {errorMsg && (
        <p className="rounded bg-destructive/10 px-2 py-1.5 text-xs text-destructive">{errorMsg}</p>
      )}

      {result && (
        <div className="rounded border bg-background p-3 text-xs space-y-1">
          <p>
            <b>{result.itemsCreated}</b> items · <b>{result.accepted}</b> serials thêm
            {result.skipped > 0 && <> · {result.skipped} bỏ qua</>}
            {result.dupStock > 0 && (
              <> · <span className="font-medium text-amber-600 dark:text-amber-400">{result.dupStock} đã có trong kho</span></>
            )}
            {result.dupSold > 0 && (
              <> · <span className="font-medium text-destructive">{result.dupSold} đã bán</span></>
            )}
            {result.failed > 0 && <> · <span className="text-destructive">{result.failed} lỗi</span></>}
          </p>
          {result.items && result.items.length > 0 && (
            <ul className="text-muted-foreground">
              {result.items.map((it) => (
                <li key={it.variantId}>
                  · {it.variantSku}: {it.serials} serials
                  {it.dupCount > 0 && ` (${it.dupCount} số trùng, không nhập)`}
                </li>
              ))}
            </ul>
          )}

          {dupTotal > 0 && (
            <div className="mt-2 space-y-2 rounded bg-amber-50 p-2 dark:bg-amber-950/30">
              <p className="text-amber-700 dark:text-amber-300">
                <b>{dupTotal} IMEI trong file đã tồn tại trong hệ thống</b> — các số này
                KHÔNG được nhập. Xuất danh sách để đối chiếu với hãng.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => exportDupsCsv(result)}>
                  Xuất danh sách trùng
                </Button>
                {fillerCandidates.map((it) => (
                  <Button
                    key={it.variantId}
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={addFillerLine.isPending}
                    title="Máy thật đếm đủ nhưng file thiếu số — thêm dòng N máy chưa rõ IMEI để tồn kho + tiền đúng; IMEI gắn bổ sung sau ở màn xem phiếu"
                    onClick={() => addFillerLine.mutate({ variantId: it.variantId, qty: it.dupCount })}
                  >
                    Hàng đếm đủ → bù {it.dupCount} máy {it.variantSku}
                  </Button>
                ))}
              </div>
              {addFillerLine.isSuccess && (
                <p className="text-green-700 dark:text-green-400">
                  Đã thêm dòng bù (chưa kèm IMEI). Sau khi xác nhận phiếu, gắn IMEI thật
                  bằng nút "Gắn IMEI bổ sung" ở màn xem phiếu.
                </p>
              )}
              {addFillerLine.isError && (
                <p className="text-destructive">{translateApiError(addFillerLine.error)}</p>
              )}
            </div>
          )}
          {failures.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-amber-600 dark:text-amber-400">
                Xem {failures.length} dòng lỗi/bỏ qua
              </summary>
              <ul className="mt-1 max-h-36 overflow-y-auto font-mono text-[11px]">
                {failures.map((r, i) => (
                  <li key={i}>
                    dòng {r.row || "—"}: {r.status} {r.key && `[${r.key}]`} {r.message && `— ${r.message}`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <Button size="sm" onClick={() => upload.mutate()} disabled={!file || upload.isPending}>
        {upload.isPending ? "Đang upload…" : "Upload"}
      </Button>
    </div>
  );
}

// Generates a minimal CSV template the user can save as xlsx. Keeping it
// client-side avoids shipping a binary asset and the BE accepts xlsx-saved
// CSVs equivalently — excelize will read either.
function downloadTemplate(single: boolean) {
  const csv = single
    ? "serial_no\n352099001761480\n352099001761481\n"
    : "variant_sku,serial_no,unit_cost\nSKU-IPH15-128,352099001761480,18000000\nSKU-IPH15-128,352099001761481,18000000\nSKU-IPH15-256,352099001761490,21000000\n";
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = single ? "serial-template-single.csv" : "serial-template-multi.csv";
  a.click();
  URL.revokeObjectURL(url);
}
